// src/lib/sw-update/update-checks.ts

import {focusManager} from '@tanstack/react-query';

/** How often to ask the browser whether a newer service worker exists. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Ask the browser to look for a newer worker on a timer, and whenever the user
 * comes back to the tab.
 *
 * The focus check is the important half: an interval alone means someone who
 * left a tab open can work a full poll period past a deploy before the browser
 * even looks. (It used to be an hour, with no focus check at all — DERP-266.)
 *
 * "Came back" is `focusManager`'s definition rather than a second pair of
 * listeners here, so this agrees with the `refetchOnWindowFocus` the query
 * client already runs on — one notification per return, so nothing needs
 * de-duplicating.
 *
 * Returns a teardown function.
 */
export function installUpdateChecks(registration: ServiceWorkerRegistration): () => void {
  const check = () => {
    // `update()` rejects when the worker script cannot be fetched, which for an
    // offline-capable app polling every few minutes is routine rather than
    // exceptional. Left unhandled it would surface as an unhandled rejection on
    // every check made while offline. There is nothing to do but wait for the
    // next one.
    void registration.update().catch(() => {});
  };

  const intervalId = setInterval(check, POLL_INTERVAL_MS);
  const unsubscribe = focusManager.subscribe((focused) => {
    if (focused) check();
  });

  return () => {
    clearInterval(intervalId);
    unsubscribe();
  };
}
